import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { Wizard } from './pages/Wizard';
import { LeasePage } from './pages/LeasePage';
import { TourPage } from './tour/TourPage';
import { TourManagePage } from './tour/TourManagePage';
import { useAuth } from './store/auth';
import { startHint, useBrand } from './lib/brand';
import { loginPathFor, validHandle } from './lib/brandSources';
import { useAppIdentity } from './lib/appIdentity';

function Protected({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const storedHint = useBrand((s) => s.hintHandle);
  const location = useLocation();
  // Keep where the resident was going (e.g. /lease from an email) and the
  // link's org hint (?o=) through sign-in.
  if (!isAuthenticated) {
    const hint = validHandle(new URLSearchParams(location.search).get('o')) ?? storedHint;
    return <Navigate to={loginPathFor(location.pathname, hint)} replace />;
  }
  return <>{children}</>;
}

/** /o/<handle>: the organization's entry link. Becomes ?o= (kept by OrgHint), then the usual flow. */
function OrgEntry() {
  const handle = validHandle(useParams().handle);
  return <Navigate to={handle ? `/?o=${handle}` : '/'} replace />;
}

/** Every URL may carry the org hint (?o=<handle>): keep it for the session and fetch its brand. */
function OrgHint() {
  const { search } = useLocation();
  useEffect(() => {
    void startHint(search);
  }, [search]);
  useAppIdentity();
  return null;
}

export default function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  // The office details carry the building's brand; load them once signed in.
  useEffect(() => {
    if (isAuthenticated) void useBrand.getState().load();
  }, [isAuthenticated]);
  return (
    <BrowserRouter>
      <OrgHint />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/o/:handle" element={<OrgEntry />} />
        {/* Public prospect pages (no sign-in): book a tour, and the link from its emails. */}
        <Route path="/o/:handle/tour" element={<TourPage />} />
        <Route path="/o/:handle/tour/manage" element={<TourManagePage />} />
        <Route
          path="/"
          element={
            <Protected>
              <Wizard />
            </Protected>
          }
        />
        <Route
          path="/lease"
          element={
            <Protected>
              <LeasePage />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
